import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ROUTES, TEXT_SNIPPETS } from "../../constants/routes";
import styles from "./Footer.module.css";

interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className }) => {
  const location = useLocation();

  // Scroll to top when page route changes (not for hash/section changes)
  useEffect(() => {
    // Only scroll to top for actual page routes, not hash changes
    if (!location.hash) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Only watch pathname, not hash

  return (
    <footer className={`${styles.footer} ${className || ""}`}>
      <div className={styles.container}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <Link to={ROUTES.HOME.path} className={styles.footerLogo}>
              <i className="fas fa-heartbeat"></i>
              <span>{ROUTES.COMPANY.NAME}</span>
            </Link>
            <p className={styles.footerDescription}>
              {ROUTES.COMPANY.DESCRIPTION}
            </p>
            <p className={styles.footerDescription}>
              <i
                className="fas fa-map-marker-alt"
                style={{ marginRight: "0.5rem" }}
              ></i>
              No. 75 Chime Avenue, New Haven, Enugu State, Nigeria
            </p>
            <div className={styles.socialLinks}>
              <a
                href={ROUTES.EXTERNAL.FACEBOOK}
                className={styles.socialLink}
                title="Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-facebook-f"></i>
              </a>
              <a
                href={ROUTES.EXTERNAL.INSTAGRAM}
                className={styles.socialLink}
                title="Instagram"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-instagram"></i>
              </a>
              <a
                href={ROUTES.EXTERNAL.TWITTER}
                className={styles.socialLink}
                title="Twitter"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-twitter"></i>
              </a>
              <a
                href={ROUTES.EXTERNAL.TIKTOK}
                className={styles.socialLink}
                title="TikTok"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-tiktok"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className={styles.footerTitle}>Company</h4>
            <ul className={styles.footerLinks}>
              <li>
                <Link to={ROUTES.ABOUT.path}>About Us</Link>
              </li>
              <li>
                <Link to={ROUTES.PRIVACY.path}>Privacy Policy</Link>
              </li>
              <li>
                <Link to={ROUTES.TERMS.path}>Terms & Conditions</Link>
              </li>
              <li>
                <a
                  href={ROUTES.EXTERNAL.EMAIL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i
                    className="fas fa-envelope"
                    style={{ marginRight: "0.5rem" }}
                  ></i>
                  {ROUTES.EXTERNAL.EMAIL_DISPLAY}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className={styles.footerTitle}>Platform</h4>
            <ul className={styles.footerLinks}>
              <li>
                <Link to={ROUTES.FEATURES.path}>Platform Features</Link>
              </li>
              <li>
                <Link to={ROUTES.FAQ.path}>FAQ</Link>
              </li>
              <li>
                <Link to={ROUTES.DONATE.path}>Make a Donation</Link>
              </li>
              <li>
                <a
                  href={ROUTES.EXTERNAL.GOOGLE_PLAY}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Mobile App
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className={styles.footerTitle}>Support</h4>
            <ul className={styles.footerLinks}>
              <li>
                <Link to={ROUTES.CONTACTUS.path}>Contact Us</Link>
              </li>
              <li>
                <a href={ROUTES.EXTERNAL.EMERGENCY_HOTLINE}>
                  <i
                    className="fas fa-phone"
                    style={{ marginRight: "0.5rem" }}
                  ></i>
                  {ROUTES.EXTERNAL.EMERGENCY_DISPLAY}
                </a>
              </li>
              <li>
                <span
                  style={{ color: "var(--success-green)", fontWeight: "600" }}
                >
                  <i
                    className="fas fa-clock"
                    style={{ marginRight: "0.5rem" }}
                  ></i>
                  24/7 Available
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <p>{TEXT_SNIPPETS.COPYRIGHT}</p>
          <p className={styles.footerNote}>{TEXT_SNIPPETS.PLATFORM_NOTE}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
